import Badge from '@codegouvfr/react-dsfr/Badge';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Tile from '@codegouvfr/react-dsfr/Tile';
import clsx from 'clsx';
import { uniqBy } from 'lodash-es';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import type { PartialSample } from 'maestro-shared/schema/Sample/Sample';
import type { SampleStatus } from 'maestro-shared/schema/Sample/SampleStatus';
import type { FunctionComponent } from 'react';
import { Link } from 'react-router';
import { assert, type Equals } from 'tsafe';
import { AuthenticatedAppRoutes } from '../../AppRoutes';
import SampleCard from '../../components/SampleCard/SampleCard';
import { useAuthentication } from '../../hooks/useAuthentication';
import ProgrammingPlanClosing from './ProgrammingPlanClosing';

type Props = {
  currentValidatedProgrammingPlan?: ProgrammingPlanChecked;
  prioritySamples?: PartialSample[];
  prioritySamplesStatus?: SampleStatus[];
  priorityProgrammingPlans?: ProgrammingPlanChecked[];
};

const DashboardPriorityActions: FunctionComponent<Props> = ({
  currentValidatedProgrammingPlan,
  prioritySamples,
  prioritySamplesStatus,
  priorityProgrammingPlans,
  ..._rest
}) => {
  assert<Equals<keyof typeof _rest, never>>();

  const { user, hasUserPermission } = useAuthentication();

  const isClosable = (programmingPlan: ProgrammingPlanChecked) =>
    hasUserPermission('manageProgrammingPlan') &&
    programmingPlan.regionalStatus.every((_) => _.status === 'Validated');

  if (!prioritySamples?.length && !priorityProgrammingPlans?.length) {
    return null;
  }

  return (
    <div className={cx('fr-col')}>
      <div
        className={clsx(
          'white-container',
          'dashboard-priority-actions-container',
          cx('fr-px-4w', 'fr-py-3w')
        )}
      >
        {priorityProgrammingPlans && priorityProgrammingPlans.length > 0 && (
          <>
            <h4 className={cx('fr-mb-1w')}>Actions prioritaires</h4>
            {uniqBy(
              priorityProgrammingPlans.filter(
                (programmingPlan) =>
                  programmingPlan.id !== currentValidatedProgrammingPlan?.id
              ),
              (programmingPlan) =>
                isClosable(programmingPlan)
                  ? programmingPlan.id
                  : programmingPlan.year
            ).map((programmingPlan) => (
              <>
                {isClosable(programmingPlan) ? (
                  <ProgrammingPlanClosing
                    programmingPlan={programmingPlan}
                    render={({ open }) => (
                      <PriorityActionCard
                        title={`Clôturer la programmation ${programmingPlan.year}`}
                        badgeLabel="Programmation"
                        description="À réaliser"
                        onClick={open}
                      />
                    )}
                  />
                ) : (
                  <PriorityActionCard
                    key={programmingPlan.id}
                    title={`Éditer la programmation ${programmingPlan.year}`}
                    badgeLabel="Programmation"
                    description="À compléter"
                    to={AuthenticatedAppRoutes.ProgrammingRoute.link({
                      year: programmingPlan.year
                    })}
                  />
                )}
              </>
            ))}
          </>
        )}
        {!priorityProgrammingPlans?.length &&
          currentValidatedProgrammingPlan &&
          prioritySamples && (
            <>
              <h4 className={cx('fr-mb-1w')}>Actions à terminer</h4>
              {(prioritySamples ?? []).length === 0 ? (
                <>Pas d'actions à terminer</>
              ) : (
                <>
                  {prioritySamples?.map((s) => (
                    <SampleCard sample={s} horizontal key={s.id} />
                  ))}
                  {prioritySamplesStatus && (
                    <div className={clsx('more-actions-link')}>
                      <Link
                        to={AuthenticatedAppRoutes.SamplesByYearRoute.link(
                          currentValidatedProgrammingPlan.year,
                          {
                            statuses: prioritySamplesStatus,
                            sampledBy: user?.id ? [user.id] : undefined
                          }
                        )}
                        className={cx('fr-link', 'fr-link--sm')}
                      >
                        Toutes les actions à terminer
                      </Link>
                    </div>
                  )}
                </>
              )}
            </>
          )}
      </div>
    </div>
  );
};

type PriorityActionCardProps = {
  title: string;
  badgeLabel: string;
  description?: string;
  label?: string;
} & (
  | {
      onClick: () => void;
      to?: never;
    }
  | { onClick?: never; to: string }
);

const PriorityActionCard = ({
  title,
  badgeLabel,
  description,
  onClick,
  to
}: PriorityActionCardProps) => {
  return (
    <Tile
      small
      orientation="horizontal"
      start={
        <Badge noIcon className={cx('fr-badge--yellow-tournesol')}>
          {badgeLabel}
        </Badge>
      }
      title={title}
      detail={description}
      {...(onClick ? { buttonProps: { onClick } } : { linkProps: { to } })}
      enlargeLinkOrButton
      titleAs="h3"
    />
  );
};

export default DashboardPriorityActions;
